import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const STORAGE_KEY = "auprea_cookie_consent_v1";

// Pages sur lesquelles la bannière ne doit JAMAIS s'afficher (landings marketing autonomes)
const SUPPRESSED_PATHS = ["/ouvrir-une-succession", "/consultation-privee-"];

function applyConsent(prefs) {
  try {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: prefs.analytics ? "granted" : "denied",
      });
    }
  } catch (e) {
    // no-op
  }
}

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const location = useLocation();
  const suppressed = SUPPRESSED_PATHS.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) {
        setOpen(true);
      } else {
        const p = JSON.parse(saved);
        applyConsent(p);
      }
    } catch (e) {
      setOpen(true);
    }
  }, []);

  const save = (prefs) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...prefs, ts: new Date().toISOString() })
      );
    } catch (e) {}
    applyConsent(prefs);
    setOpen(false);
  };

  const acceptAll = () => save({ analytics: true });
  const refuseAll = () => save({ analytics: false });
  const acceptChoice = () => save({ analytics });

  if (!open) return null;
  if (suppressed) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-live="polite"
      aria-label="Préférences cookies"
      style={{
        position: "fixed",
        zIndex: 9999,
        left: 16,
        right: 16,
        bottom: 16,
        maxWidth: 880,
        margin: "0 auto",
        backgroundColor: "var(--auprea-navy)",
        color: "rgba(243,239,230,0.95)",
        borderRadius: 14,
        boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
        border: "1px solid rgba(202,168,106,0.25)",
        padding: "20px 22px",
        fontSize: 14,
        lineHeight: 1.55,
      }}
    >
      <div
        style={{
          color: "var(--auprea-gold)",
          fontSize: 11,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          fontWeight: 700,
          marginBottom: 8,
        }}
      >
        — Vos préférences cookies
      </div>
      <div style={{ fontFamily: "Spectral, serif", fontSize: 18, color: "#fff", marginBottom: 8 }}>
        Respecter votre vie privée
      </div>
      <p style={{ margin: "0 0 14px 0" }}>
        AUPREA utilise des cookies strictement nécessaires au fonctionnement du site (connexion,
        sécurité). Avec votre accord, nous utilisons aussi des cookies de mesure d'audience
        (Google Analytics, IP anonymisée) pour améliorer le service. Vous pouvez modifier votre
        choix à tout moment depuis la{" "}
        <Link
          to="/legal/confidentialite"
          style={{ color: "var(--auprea-gold)", textDecoration: "underline" }}
        >
          Politique de confidentialité
        </Link>
        .
      </p>

      {showDetails && (
        <div
          style={{
            backgroundColor: "rgba(243,239,230,0.06)",
            border: "1px solid rgba(202,168,106,0.2)",
            borderRadius: 10,
            padding: 14,
            marginBottom: 14,
          }}
          data-testid="cookie-consent-details"
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
            <input type="checkbox" checked disabled style={{ marginTop: 4 }} />
            <div>
              <div style={{ fontWeight: 600, color: "#fff" }}>Cookies essentiels (obligatoires)</div>
              <div style={{ fontSize: 13, color: "rgba(243,239,230,0.7)" }}>
                Authentification, sécurité, préférences de session. Indispensables au fonctionnement.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <input
              type="checkbox"
              checked={analytics}
              onChange={(e) => setAnalytics(e.target.checked)}
              style={{ marginTop: 4 }}
              data-testid="cookie-consent-toggle-analytics"
              id="cookie-analytics"
            />
            <div>
              <label htmlFor="cookie-analytics" style={{ fontWeight: 600, color: "#fff", cursor: "pointer" }}>
                Mesure d'audience (Google Analytics)
              </label>
              <div style={{ fontSize: 13, color: "rgba(243,239,230,0.7)" }}>
                Statistiques anonymes (pages vues, durée). IP anonymisée. Aucune donnée publicitaire.
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          data-testid="cookie-consent-customize"
          style={{
            background: "transparent",
            color: "rgba(243,239,230,0.85)",
            border: "1px solid rgba(243,239,230,0.3)",
            borderRadius: 999,
            padding: "10px 18px",
            cursor: "pointer",
            fontSize: 13,
          }}
        >
          {showDetails ? "Masquer les détails" : "Personnaliser"}
        </button>
        <button
          type="button"
          onClick={refuseAll}
          data-testid="cookie-consent-refuse"
          style={{
            background: "transparent",
            color: "#fff",
            border: "1px solid rgba(243,239,230,0.5)",
            borderRadius: 999,
            padding: "10px 18px",
            cursor: "pointer",
            fontSize: 13,
          }}
        >
          Tout refuser
        </button>
        {showDetails ? (
          <button
            type="button"
            onClick={acceptChoice}
            data-testid="cookie-consent-save"
            style={{
              background: "var(--auprea-gold)",
              color: "var(--auprea-navy)",
              border: "none",
              borderRadius: 999,
              padding: "10px 20px",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            Enregistrer mes choix
          </button>
        ) : (
          <button
            type="button"
            onClick={acceptAll}
            data-testid="cookie-consent-accept"
            style={{
              background: "var(--auprea-gold)",
              color: "var(--auprea-navy)",
              border: "none",
              borderRadius: 999,
              padding: "10px 20px",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            Tout accepter
          </button>
        )}
      </div>
    </div>
  );
}
